import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, CheckCircle, ArrowLeft, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import '../styles/ResultSubmission.css';

const API_BASE = process.env.REACT_APP_API_URL || '';

const SUBJECTS_CONFIG = {
  linked: ['Maths', 'Science'],
  optional: ['Social Science'],
};

const ALL_SUBJECTS = [...SUBJECTS_CONFIG.linked, ...SUBJECTS_CONFIG.optional];

const SUBJECT_DISPLAY_LABELS = {
  Maths: 'Maths',
  Science: 'Science',
  'Social Science': 'SST',
};

const ResultSubmission = () => {
  const navigate = useNavigate();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [schoolName, setSchoolName] = useState('');
  const [studentClass, setStudentClass] = useState('');
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [marks, setMarks] = useState({});
  const [resultScreenshot, setResultScreenshot] = useState(null);
  const [passportPhoto, setPassportPhoto] = useState(null);
  const [screenshotPreview, setScreenshotPreview] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');

  const screenshotRef = useRef(null);
  const photoRef = useRef(null);

  const handleSubjectToggle = (subject) => {
    const isLinked = SUBJECTS_CONFIG.linked.includes(subject);

    setSelectedSubjects((prev) => {
      if (isLinked) {
        const hasLinked = prev.includes(SUBJECTS_CONFIG.linked[0]);
        if (hasLinked) {
          // Remove both linked subjects
          const next = prev.filter((s) => !SUBJECTS_CONFIG.linked.includes(s));
          setMarks((m) => {
            const updated = { ...m };
            SUBJECTS_CONFIG.linked.forEach((s) => delete updated[s]);
            return updated;
          });
          return next;
        } else {
          // Add both linked subjects
          return [...prev, ...SUBJECTS_CONFIG.linked];
        }
      } else {
        // Toggle optional subject
        if (prev.includes(subject)) {
          setMarks((m) => {
            const updated = { ...m };
            delete updated[subject];
            return updated;
          });
          return prev.filter((s) => s !== subject);
        } else {
          return [...prev, subject];
        }
      }
    });
  };

  const handleMarksChange = (subject, value) => {
    const numValue = value.replace(/\D/g, '');
    if (numValue === '' || (parseInt(numValue) >= 0 && parseInt(numValue) <= 100)) {
      setMarks((prev) => ({ ...prev, [subject]: numValue }));
    }
  };

  const handleFileChange = (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowed = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowed.includes(file.type)) {
      setError('Please upload a JPEG, PNG, or WebP image.');
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      setError('File must be under 50MB.');
      return;
    }

    setError('');
    const previewUrl = URL.createObjectURL(file);

    if (type === 'screenshot') {
      setResultScreenshot(file);
      setScreenshotPreview(previewUrl);
    } else {
      setPassportPhoto(file);
      setPhotoPreview(previewUrl);
    }
  };

  const removeFile = (type) => {
    if (type === 'screenshot') {
      setResultScreenshot(null);
      if (screenshotPreview) URL.revokeObjectURL(screenshotPreview);
      setScreenshotPreview(null);
      if (screenshotRef.current) screenshotRef.current.value = '';
    } else {
      setPassportPhoto(null);
      if (photoPreview) URL.revokeObjectURL(photoPreview);
      setPhotoPreview(null);
      if (photoRef.current) photoRef.current.value = '';
    }
  };

  const isFormValid = () => {
    if (!firstName.trim() || !lastName.trim() || !schoolName.trim()) return false;
    if (!studentClass) return false;
    if (selectedSubjects.length === 0) return false;
    for (const subj of selectedSubjects) {
      if (!marks[subj] || marks[subj] === '') return false;
    }
    if (!resultScreenshot || !passportPhoto) return false;
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isFormValid()) {
      setError('Please fill all required fields.');
      return;
    }

    setIsSubmitting(true);
    setError('');

    const formData = new FormData();
    formData.append('first_name', firstName.trim());
    formData.append('last_name', lastName.trim());
    formData.append('school_name', schoolName.trim());
    formData.append('student_class', studentClass);
    formData.append('subjects', JSON.stringify(selectedSubjects));
    formData.append('marks', JSON.stringify(
      Object.fromEntries(selectedSubjects.map((s) => [s, marks[s]]))
    ));
    formData.append('result_screenshot', resultScreenshot);
    formData.append('passport_photo', passportPhoto);

    try {
      const res = await fetch(`${API_BASE}/api/results/submit`, {
        method: 'POST',
        body: formData,
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Submission failed');
      }

      setIsSuccess(true);
      setTimeout(() => {
        navigate('/');
      }, 5000);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="result-submission-page">
        <div className="success-overlay">
          <div className="success-content">
            <div className="success-checkmark">
              <CheckCircle size={80} />
            </div>
            <h2 className="heading-2">Submitted Successfully!</h2>
            <p className="body-text">
              Thank you for submitting your results. Redirecting you to the homepage...
            </p>
            <div className="success-progress-bar">
              <div className="success-progress-fill" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="result-submission-page">
      <div className="result-submission-container">
        {/* Header */}
        <div className="result-header">
          <button className="back-button" onClick={() => navigate('/')}>
            <ArrowLeft size={20} />
            <span>Back to Home</span>
          </button>
          <div className="result-header-brand">
            <img src="/images/logos/logo-hero.png" alt="Krishna's Academy" className="result-logo" />
            <div className="result-header-text">
              <h1 className="heading-2">Krishna's Academy</h1>
              <p className="result-tagline">We Shape Your Future</p>
            </div>
          </div>
          <h2 className="result-page-title">Submit Your 2025-26 Board Results</h2>
        </div>

        <form onSubmit={handleSubmit} className="result-form">
          <fieldset disabled={isSubmitting} className="result-fieldset">
          {/* Personal Information */}
          <Card className="form-section-card">
            <CardContent className="form-section">
              <h3 className="form-section-title">Personal Information</h3>
              <div className="form-grid-2">
                <div className="form-group">
                  <label htmlFor="firstName">First Name *</label>
                  <input
                    type="text"
                    id="firstName"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="Enter your first name"
                    required
                    maxLength={100}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="lastName">Last Name *</label>
                  <input
                    type="text"
                    id="lastName"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="Enter your last name"
                    required
                    maxLength={100}
                  />
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="schoolName">School Name *</label>
                <input
                  type="text"
                  id="schoolName"
                  value={schoolName}
                  onChange={(e) => setSchoolName(e.target.value)}
                  placeholder="Enter your school name"
                  required
                  maxLength={200}
                />
              </div>
            </CardContent>
          </Card>

          {/* Class Selection */}
          <Card className="form-section-card">
            <CardContent className="form-section">
              <h3 className="form-section-title">Select Class *</h3>
              <div className="class-selector">
                {['10th', '12th'].map((cls) => (
                  <button
                    key={cls}
                    type="button"
                    className={`class-option ${studentClass === cls ? 'active' : ''} ${cls === '12th' ? 'disabled' : ''}`}
                    disabled={cls === '12th'}
                    onClick={() => {
                      setStudentClass(cls);
                      setSelectedSubjects([]);
                      setMarks({});
                    }}
                  >
                    Class {cls}
                    {cls === '12th' && <span className="coming-soon-badge">To Be Released</span>}
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Subject Selection */}
          {studentClass && (
            <Card className="form-section-card">
              <CardContent className="form-section">
                <h3 className="form-section-title">Select Subjects *</h3>
                <div className="subject-chips">
                  {ALL_SUBJECTS.map((subject) => {
                    const isSelected = selectedSubjects.includes(subject);
                    const isLinked = SUBJECTS_CONFIG.linked.includes(subject);
                    return (
                      <button
                        key={subject}
                        type="button"
                        className={`subject-chip ${isSelected ? 'selected' : ''} ${isLinked ? 'linked' : 'optional'}`}
                        onClick={() => handleSubjectToggle(subject)}
                      >
                        {SUBJECT_DISPLAY_LABELS[subject] || subject}
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Marks Entry */}
          {selectedSubjects.length > 0 && (
            <Card className="form-section-card">
              <CardContent className="form-section">
                <h3 className="form-section-title">Enter Marks (out of 100) *</h3>
                <div className="marks-grid">
                  {selectedSubjects.map((subject) => (
                    <div key={subject} className="marks-input-group">
                      <label htmlFor={`marks-${subject}`}>{SUBJECT_DISPLAY_LABELS[subject] || subject}</label>
                      <div className="marks-input-wrapper">
                        <input
                          type="text"
                          inputMode="numeric"
                          id={`marks-${subject}`}
                          value={marks[subject] || ''}
                          onChange={(e) => handleMarksChange(subject, e.target.value)}
                          placeholder="0"
                          maxLength={3}
                          required
                        />
                        <span className="marks-suffix">/ 100</span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* File Uploads */}
          <Card className="form-section-card">
            <CardContent className="form-section">
              <h3 className="form-section-title">Upload Documents *</h3>
              <div className="upload-grid">
                {/* Result Screenshot */}
                <div className="upload-area">
                  <label>Result Screenshot *</label>
                  {screenshotPreview ? (
                    <div className="upload-preview">
                      <img src={screenshotPreview} alt="Result preview" />
                      <button
                        type="button"
                        className="remove-file-btn"
                        onClick={() => removeFile('screenshot')}
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ) : (
                    <div
                      className="upload-dropzone"
                      onClick={() => screenshotRef.current?.click()}
                    >
                      <Upload size={32} />
                      <p>Click to upload result screenshot</p>
                      <span className="upload-hint">JPEG, PNG or WebP (max 50MB)</span>
                    </div>
                  )}
                  <input
                    ref={screenshotRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={(e) => handleFileChange(e, 'screenshot')}
                    hidden
                  />
                </div>

                {/* Passport Photo */}
                <div className="upload-area">
                  <label>Passport Size Photo *</label>
                  {photoPreview ? (
                    <div className="upload-preview passport-preview">
                      <img src={photoPreview} alt="Passport preview" />
                      <button
                        type="button"
                        className="remove-file-btn"
                        onClick={() => removeFile('photo')}
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ) : (
                    <div
                      className="upload-dropzone"
                      onClick={() => photoRef.current?.click()}
                    >
                      <Upload size={32} />
                      <p>Click to upload passport photo</p>
                      <span className="upload-hint">JPEG, PNG or WebP (max 50MB)</span>
                    </div>
                  )}
                  <input
                    ref={photoRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={(e) => handleFileChange(e, 'photo')}
                    hidden
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          </fieldset>

          {/* Error Message */}
          {error && (
            <div className="form-error">
              <p>{error}</p>
            </div>
          )}

          {/* Submit Button */}
          <Button
            type="submit"
            className="submit-button"
            disabled={!isFormValid() || isSubmitting}
          >
            {isSubmitting ? (
              <span className="submit-loading">
                <span className="spinner" />
                Submitting...
              </span>
            ) : (
              'Submit Results'
            )}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default ResultSubmission;
